import React, { useEffect, useMemo, useState, useCallback } from "react";
import { X, Search, CheckSquare, Square } from "lucide-react";
import API_BASE_URL from "../../../services/api";

/* ---------------- helpers ---------------- */
const asNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// Tests (from /api/tests/search) => grouped object in response.data
const normalizeTest = (t, category) => {
  const id = Number(t?.id);
  return {
    ...t,
    id,
    uid: `test-${id}`,
    type: "test",
    category,
    price: asNumber(t?.finalPrice ?? t?.price ?? 0),
  };
};

// Checkups/Packages (from /api/checkups) => array in response.data
const normalizeCheckup = (p, categoryName) => {
  const id = Number(p?.id);
  return {
    ...p,
    id,
    uid: `package-${id}`,
    type: "package",
    category: categoryName,
    price: asNumber(
      p?.offerPrice ?? p?.finalPrice ?? p?.price ?? p?.actualPrice ?? 0
    ),
  };
};

const extractTestsGrouped = (json) => {
  const grouped = json?.data;
  return grouped && typeof grouped === "object" ? grouped : {};
};

const groupCheckupsByCategory = (arr) => {
  const grouped = {};
  (arr || []).forEach((p) => {
    const categoryName =
      p?.category?.name ||
      p?.categoryName ||
      (p?.categoryId ? `Category ${p.categoryId}` : "Checkups");

    grouped[categoryName] = grouped[categoryName] || [];
    grouped[categoryName].push(normalizeCheckup(p, categoryName));
  });
  return grouped;
};

/* ---------------- small reusable list UI ---------------- */
const CategoryList = ({
  grouped,
  searchQuery,
  setSearchQuery,
  selected,
  setSelected,
  expanded,
  setExpanded,
  loading,
  emptyText = "No items found",
}) => {
  const allItems = useMemo(() => Object.values(grouped).flat(), [grouped]);
  const totalCount = allItems.length;
  const selectedCount = selected.length;

  const filteredGrouped = useMemo(() => {
    if (!searchQuery.trim()) return grouped;

    const term = searchQuery.toLowerCase();
    const filtered = {};

    Object.keys(grouped).forEach((category) => {
      const items = (grouped[category] || []).filter((item) => {
        const nameMatch = String(item.name || "").toLowerCase().includes(term);
        const codeMatch = item.code
          ? String(item.code).toLowerCase().includes(term)
          : false;
        const categoryMatch = category.toLowerCase().includes(term);
        const akaMatch = item.alsoKnowAs
          ? String(item.alsoKnowAs).toLowerCase().includes(term)
          : false;
        return nameMatch || codeMatch || categoryMatch || akaMatch;
      });

      if (items.length > 0) filtered[category] = items;
    });

    return filtered;
  }, [grouped, searchQuery]);

  const toggleItem = (item) => {
    setSelected((prev) => {
      const exists = prev.some((s) => s.uid === item.uid);
      return exists ? prev.filter((s) => s.uid !== item.uid) : [...prev, item];
    });
  };

  const toggleCategory = (category) => {
    const items = grouped[category] || [];
    const allSelected =
      items.length > 0 && items.every((i) => selected.some((s) => s.uid === i.uid));

    setSelected((prev) => {
      if (allSelected) return prev.filter((x) => x.category !== category);
      const toAdd = items.filter((i) => !prev.some((p) => p.uid === i.uid));
      return [...prev, ...toAdd];
    });
  };

  const toggleExpand = (category) => {
    setExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const toggleSelectAll = () => {
    const allSelected =
      totalCount > 0 && allItems.every((i) => selected.some((s) => s.uid === i.uid));
    setSelected(allSelected ? [] : allItems);
  };

  const filteredKeys = Object.keys(filteredGrouped);

  return (
    <div className="pb-4">
      {/* Search + Bulk */}
      <div className="mt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, code, category..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded"
          >
            {selectedCount === totalCount && totalCount > 0 ? (
              <CheckSquare className="w-3 h-3" />
            ) : (
              <Square className="w-3 h-3" />
            )}
            {selectedCount === totalCount && totalCount > 0 ? "Deselect All" : "Select All"}
          </button>

          <div className="text-xs text-gray-500">
            {selectedCount} selected • {filteredKeys.length} categories
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mt-4">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : filteredKeys.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-2">{emptyText}</div>
            <p className="text-sm text-gray-500">Try a different search term</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredKeys.map((category) => {
              const categoryItems = filteredGrouped[category] || [];
              const selectedInCategory = categoryItems.filter((i) =>
                selected.some((s) => s.uid === i.uid)
              ).length;
              const allSelected =
                categoryItems.length > 0 && selectedInCategory === categoryItems.length;

              return (
                <div key={category} className="border rounded-lg overflow-hidden">
                  <div
                    className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleExpand(category)}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(category);
                        }}
                        className="w-5 h-5 flex items-center justify-center"
                      >
                        {allSelected ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : selectedInCategory > 0 ? (
                          <div className="relative">
                            <Square className="w-4 h-4 text-gray-400" />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-2 h-2 bg-blue-600 rounded-sm" />
                            </div>
                          </div>
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </button>

                      <h4 className="text-sm font-semibold text-gray-900">{category}</h4>
                      <span className="text-xs text-gray-500">
                        ({selectedInCategory}/{categoryItems.length})
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                        {categoryItems.length} items
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-500 transition-transform ${
                          expanded[category] ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>

                  {expanded[category] && (
                    <div className="divide-y">
                      {categoryItems.map((item) => {
                        const isSelected = selected.some((s) => s.uid === item.uid);

                        return (
                          <div
                            key={item.uid}
                            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleItem(item)}
                          >
                            <div className="flex items-center gap-3">
                              {isSelected ? (
                                <CheckSquare className="w-4 h-4 text-blue-600" />
                              ) : (
                                <Square className="w-4 h-4 text-gray-400" />
                              )}

                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm text-gray-900">{item.name}</span>
                                  {item.code && (
                                    <span className="text-xs text-gray-500">({item.code})</span>
                                  )}
                                </div>
                                {item.description && (
                                  <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-4">
                              <span className="text-sm font-semibold text-green-600 whitespace-nowrap">
                                ₹{item.price}
                              </span>
                              <span
                                className={`text-[10px] px-2 py-0.5 rounded ${
                                  item.type === "package"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {item.type === "package" ? "CHECKUP" : "TEST"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- main modal ---------------- */
const BrowseTestsModal = ({ isOpen, onClose, onAddTests }) => {
  const [activeTab, setActiveTab] = useState("tests"); // "tests" | "checkups"
  const [testsGrouped, setTestsGrouped] = useState({});
  const [checkupsGrouped, setCheckupsGrouped] = useState({});
  const [loading, setLoading] = useState({ tests: true, checkups: true });
  const [search, setSearch] = useState({ tests: "", checkups: "" });
  const [selected, setSelected] = useState({ tests: [], checkups: [] });
  const [expanded, setExpanded] = useState({ tests: {}, checkups: {} });

  useEffect(() => {
    if (!isOpen) return;
    setSelected({ tests: [], checkups: [] });
    setSearch({ tests: "", checkups: "" });

    // keep try/catch inside fetch functions
    fetchTests();
    fetchCheckups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchTests = useCallback(async () => {
    setLoading((p) => ({ ...p, tests: true }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/tests/search`, {
        headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      const grouped = extractTestsGrouped(json);

      const merged = {};
      Object.keys(grouped).forEach((category) => {
        const arr = Array.isArray(grouped[category]) ? grouped[category] : [];
        merged[category] = arr.map((t) => normalizeTest(t, category));
      });

      setTestsGrouped(merged);

      const ex = {};
      Object.keys(merged).forEach((c) => (ex[c] = true));
      setExpanded((p) => ({ ...p, tests: ex }));
    } catch (e) {
      console.error("tests fetch error:", e);
      setTestsGrouped({});
      setExpanded((p) => ({ ...p, tests: {} }));
    } finally {
      setLoading((p) => ({ ...p, tests: false }));
    }
  }, []);

  const fetchCheckups = useCallback(async () => {
    setLoading((p) => ({ ...p, checkups: true }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/checkups`, {
        headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      const arr = Array.isArray(json?.data) ? json.data : [];

      const grouped = groupCheckupsByCategory(arr);
      setCheckupsGrouped(grouped);

      const ex = {};
      Object.keys(grouped).forEach((c) => (ex[c] = true));
      setExpanded((p) => ({ ...p, checkups: ex }));
    } catch (e) {
      console.error("checkups fetch error:", e);
      setCheckupsGrouped({});
      setExpanded((p) => ({ ...p, checkups: {} }));
    } finally {
      setLoading((p) => ({ ...p, checkups: false }));
    }
  }, []);

  const totalSelected = selected.tests.length + selected.checkups.length;

  const handleAddSelected = () => {
    try {
      const all = [...selected.tests, ...selected.checkups];
      if (all.length === 0) return alert("Please select at least one item");
      onAddTests(all);
      onClose();
    } catch (e) {
      console.error("handleAddSelected error:", e);
      alert("Something went wrong while adding items.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Centered modal */}
      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-4">
        {/* IMPORTANT: max height + flex column */}
        <div className="w-full max-w-5xl bg-white rounded-lg shadow-xl border flex flex-col max-h-[90vh]">
          {/* Header (always visible) */}
          <div className="px-6 py-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Browse</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {totalSelected} item{totalSelected !== 1 ? "s" : ""} selected
                </p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("tests")}
                className={`px-4 py-2 text-sm rounded border ${
                  activeTab === "tests"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Tests
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("checkups")}
                className={`px-4 py-2 text-sm rounded border ${
                  activeTab === "checkups"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Checkups
              </button>
            </div>
          </div>

          {/* Body scroll (this is the key fix) */}
          <div className="flex-1 overflow-y-auto px-6">
            {activeTab === "tests" ? (
              <CategoryList
                grouped={testsGrouped}
                loading={loading.tests}
                searchQuery={search.tests}
                setSearchQuery={(v) => setSearch((p) => ({ ...p, tests: v }))}
                selected={selected.tests}
                setSelected={(fn) =>
                  setSelected((p) => ({
                    ...p,
                    tests: typeof fn === "function" ? fn(p.tests) : fn,
                  }))
                }
                expanded={expanded.tests}
                setExpanded={(fn) =>
                  setExpanded((p) => ({
                    ...p,
                    tests: typeof fn === "function" ? fn(p.tests) : fn,
                  }))
                }
                emptyText="No tests found"
              />
            ) : (
              <CategoryList
                grouped={checkupsGrouped}
                loading={loading.checkups}
                searchQuery={search.checkups}
                setSearchQuery={(v) => setSearch((p) => ({ ...p, checkups: v }))}
                selected={selected.checkups}
                setSelected={(fn) =>
                  setSelected((p) => ({
                    ...p,
                    checkups: typeof fn === "function" ? fn(p.checkups) : fn,
                  }))
                }
                expanded={expanded.checkups}
                setExpanded={(fn) =>
                  setExpanded((p) => ({
                    ...p,
                    checkups: typeof fn === "function" ? fn(p.checkups) : fn,
                  }))
                }
                emptyText="No checkups found"
              />
            )}
          </div>

          {/* Footer (always visible now) */}
          <div className="px-6 py-4 border-t shrink-0 flex justify-between items-center bg-white">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{totalSelected}</span> selected
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddSelected}
                disabled={totalSelected === 0}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Selected ({totalSelected})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowseTestsModal;
